// src/components/join/joinContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase-client';
import { calculateTotalFees, normalizeCadenceDiscountPct } from '../../utils/calculateTotalFees';
import { resolveCycleBasePricing, shouldProrateMembershipYear } from '../../utils/subscriptionPricing';
import { getMembershipYearWindow } from '../../utils/membershipYear';
import { isWaitlistJoinRoutingEnabled, normalizePendingMemberStatus } from '../../utils/waitlist';
import { getDocumentSourceUrl, getRequiredJoinDocuments } from '../teams/utils/documentUtils';
import { buildRequiredTagGroups, getPricingTagNames } from '../../utils/requiredTags';
const JoinContext = createContext();
const PRICING_QUESTION_IDS = ['pricingTags', 'pricingTag'];
const AUTH_RESOLUTION_FALLBACK_MS = 5000;
const isPricingQuestion = q => PRICING_QUESTION_IDS.includes(q?.id);

function waitForAuthResolution() {
  return new Promise((resolve) => {
    let settled = false;
    let unsubscribe = () => {};
    const finish = (currentUser) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      unsubscribe();
      resolve(currentUser);
    };
    const timeoutId = setTimeout(() => {
      finish(auth.currentUser);
    }, AUTH_RESOLUTION_FALLBACK_MS);
    unsubscribe = auth.onAuthStateChanged((currentUser) => {
      finish(currentUser);
    }, () => {
      finish(auth.currentUser);
    });
  });
}

/* ──────────────────────────────────────────────────────────────
   Cadence helpers
   STRICT: only ever Monthly or Yearly
   ────────────────────────────────────────────────────────────── */
function buildCadenceOptions(setup) {
  const mode = setup?.cadenceMode || 'fixed';
  if (mode === 'fixed') {
    const unit = setup?.fixedInterval?.unit === 'month' ? 'month' : 'year';
    return [{
      key: unit === 'month' ? 'monthly' : 'yearly',
      label: unit === 'month' ? 'Monthly' : 'Yearly',
      unit,
      count: 1,
      discountPct: 0,
      fixed: true
    }];
  }

  // member_choice → always both monthly & yearly, discounts from setup (default 0)
  const mc = setup?.memberChoice || {
    monthly: {
      discountPct: 0
    },
    yearly: {
      discountPct: 0
    }
  };
  return [{
    key: 'monthly',
    label: 'Monthly',
    unit: 'month',
    count: 1,
    discountPct: normalizeCadenceDiscountPct(mc?.monthly?.discountPct),
    fixed: false
  }, {
    key: 'yearly',
    label: 'Yearly',
    unit: 'year',
    count: 1,
    discountPct: normalizeCadenceDiscountPct(mc?.yearly?.discountPct),
    fixed: false
  }];
}
function pickDefaultCadence(options) {
  if (!options?.length) return null;
  return options.find(o => o.key === 'yearly') || options.find(o => o.key === 'monthly') || options[0];
}
function formatFamilyMemberName(profile = {}, uid = '') {
  const first = typeof profile?.firstName === 'string' ? profile.firstName.trim() : '';
  const last = typeof profile?.lastName === 'string' ? profile.lastName.trim() : '';
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const display = typeof profile?.displayName === 'string' ? profile.displayName.trim() : '';
  if (display) return display;
  const username = typeof profile?.username === 'string' ? profile.username.trim() : '';
  if (username) return username;
  return uid || 'Family member';
}
function parseTargetIds(value, fallbackUid = '', includeFallback = true) {
  const parseArray = raw => {
    const normalized = (Array.isArray(raw) ? raw : []).map(item => typeof item === 'string' ? item.trim() : '').filter(Boolean);
    if (includeFallback && fallbackUid && !normalized.includes(fallbackUid)) {
      normalized.unshift(fallbackUid);
    }
    return Array.from(new Set(normalized));
  };
  if (Array.isArray(value)) return parseArray(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return includeFallback && fallbackUid ? [fallbackUid] : [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parseArray(parsed);
    } catch {
      // fall through to csv parsing
    }
    return parseArray(trimmed.split(','));
  }
  return includeFallback && fallbackUid ? [fallbackUid] : [];
}
function parseTagNames(value) {
  const normalizeArray = raw => Array.from(new Set((Array.isArray(raw) ? raw : []).map(tag => typeof tag === 'string' ? tag.trim() : '').filter(Boolean)));
  if (Array.isArray(value)) return normalizeArray(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return normalizeArray(parsed);
    } catch {
      // fall through to csv parsing
    }
    return normalizeArray(trimmed.split(','));
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).filter(([, enabled]) => Boolean(enabled)).map(([tag]) => String(tag || '').trim()).filter(Boolean);
  }
  return [];
}
function normalizeMembershipPlanType(value) {
  return value === 'family_tier' ? 'family_tier' : 'individual';
}
function parseFamilyTiers(setup = {}) {
  const tiers = Array.isArray(setup?.familyTiers) ? setup.familyTiers : [];
  return tiers.map((tier, index) => ({
    id: typeof tier?.id === 'string' && tier.id.trim() ? tier.id.trim() : `family_tier_${index + 1}`,
    label: typeof tier?.label === 'string' && tier.label.trim() ? tier.label.trim() : `Family ${index + 1}`,
    minMembers: Math.max(1, Number(tier?.minMembers || 1)),
    maxParents: Math.max(1, Number(tier?.maxParents || 1)),
    maxChildren: Math.max(0, Number(tier?.maxChildren || 0)),
    childMaxAge: tier.childMaxAge || null,
    childAgeCutoff: tier.childAgeCutoff || 'renewal',
    baseRate: Number(tier?.baseRate || 0),
    baseRateYearly: Number(tier?.baseRateYearly || 0),
    sortOrder: Number.isFinite(Number(tier?.sortOrder)) ? Number(tier.sortOrder) : index
  })).sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.label.localeCompare(b.label);
  });
}

function hasAcceptedAllRequiredJoinDocuments(requiredDocs = [], pendingData = {}) {
  if (!Array.isArray(requiredDocs) || requiredDocs.length === 0) return true;
  const acceptedDocs = Array.isArray(pendingData?.documentAcceptances) ? pendingData.documentAcceptances : [];
  return requiredDocs.every(requiredDoc => {
    const requiredId = typeof requiredDoc?.id === 'string' ? requiredDoc.id.trim() : '';
    if (!requiredId) return false;
    const requiredVersionLabel = typeof requiredDoc?.versionLabel === 'string' ? requiredDoc.versionLabel.trim() : '';
    const requiredSourceUrl = getDocumentSourceUrl(requiredDoc);
    return acceptedDocs.some(acceptedDoc => {
      const acceptedId = typeof acceptedDoc?.documentId === 'string' ? acceptedDoc.documentId.trim() : '';
      const acceptedVersionLabel = typeof acceptedDoc?.versionLabel === 'string' ? acceptedDoc.versionLabel.trim() : '';
      const acceptedSourceUrl = typeof acceptedDoc?.sourceUrl === 'string' ? acceptedDoc.sourceUrl.trim() : '';
      return acceptedId === requiredId
        && acceptedVersionLabel === requiredVersionLabel
        && acceptedSourceUrl === requiredSourceUrl;
    });
  });
}

function preserveLearnToRowProgressStatus(currentStatus) {
  return currentStatus === 'payment' || currentStatus === 'joined' ? currentStatus : 'form';
}

/* ────────────────────────────────────────────────────────────── */

export function JoinProvider({
  teamId,
  children,
  isDemoMode = false,
  isLearnToRowMode = false,
  isPrelaunchCommitteeMode = false
}) {
  const [team, setTeam] = useState(null);
  const [user, setUser] = useState(auth.currentUser);
  const [verifiedPrelaunchCommittee, setVerifiedPrelaunchCommittee] = useState(false);
  const [status, setStatus] = useState('loading');
  const [formResponses, setFormResponses] = useState({});
  const [questions, setQuestions] = useState([]);
  const [requiredJoinDocuments, setRequiredJoinDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [stripeSetup, setStripeSetup] = useState(null);

  // Fees exposed to UI
  const [stripeAmount, setStripeAmount] = useState(null);
  const [stripeFeeBreakdown, setStripeFeeBreakdown] = useState(null);
  const [hasPaid, setHasPaid] = useState(false);
  const [hasPendingMemberDoc, setHasPendingMemberDoc] = useState(false);
  const [pendingMemberStatus, setPendingMemberStatus] = useState(null);

  // Cadence UI
  const [selectedCadence, setSelectedCadence] = useState(null); // {key,label,unit,count,discountPct}
  const [joinPaymentSummary, setJoinPaymentSummary] = useState(null);
  const [lastAppliedTags, setLastAppliedTags] = useState([]); // used for fee recompute when cadence changes
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [familyMembershipGroups, setFamilyMembershipGroups] = useState([]);
  const [selectedFamilyMemberIds, setSelectedFamilyMemberIds] = useState([]);
  const [selectedIndividualTargetFamilyId, setSelectedIndividualTargetFamilyId] = useState('');
  const [selectedIndividualTargetUserId, setSelectedIndividualTargetUserId] = useState('');
  const [membershipPlanType, setMembershipPlanType] = useState('individual');
  const [selectedFamilyTierId, setSelectedFamilyTierId] = useState('');
  const [membershipPricingTag, setMembershipPricingTag] = useState('');
  const [membershipDiscountCode, setMembershipDiscountCode] = useState('');
  const [preassignedTags, setPreassignedTags] = useState([]);
  const [familySelectionLoading, setFamilySelectionLoading] = useState(false);
  const [familySelectionError, setFamilySelectionError] = useState(null);

  // Keep the latest questions available without forcing effects to re-run on length changes
  const questionsRef = useRef([]);
  const requiredJoinDocumentsRef = useRef([]);
  const membershipPlanTypeRef = useRef(membershipPlanType);
  const selectedFamilyTierIdRef = useRef(selectedFamilyTierId);
  const selectedCadenceRef = useRef(selectedCadence);
  const defaultCadenceRef = useRef(null);
  const cadenceOptionsRef = useRef([]);
  const membershipTargetCountRef = useRef(1);
  const computeFeesRef = useRef(null);
  useEffect(() => {
    if (!isPrelaunchCommitteeMode || !teamId || !user?.uid) {
      setVerifiedPrelaunchCommittee(false);
      return undefined;
    }
    let cancelled = false;
    httpsCallable(functions, 'getMyPrelaunchCommitteeStatus')({ teamId })
      .then((result) => {
        if (!cancelled) setVerifiedPrelaunchCommittee(result.data?.isPrelaunchCommittee === true);
      })
      .catch(() => {
        if (!cancelled) setVerifiedPrelaunchCommittee(false);
      });
    return () => { cancelled = true; };
  }, [isPrelaunchCommitteeMode, teamId, user?.uid]);
  useEffect(() => {
    questionsRef.current = questions;
  }, [questions]);
  useEffect(() => {
    requiredJoinDocumentsRef.current = requiredJoinDocuments;
  }, [requiredJoinDocuments]);
  useEffect(() => {
    membershipPlanTypeRef.current = membershipPlanType;
  }, [membershipPlanType]);
  useEffect(() => {
    selectedFamilyTierIdRef.current = selectedFamilyTierId;
  }, [selectedFamilyTierId]);
  useEffect(() => {
    selectedCadenceRef.current = selectedCadence;
  }, [selectedCadence]);

  // Compute cadence options & default based on current setup (memoised)
  const cadenceOptions = useMemo(() => buildCadenceOptions(stripeSetup || {}), [stripeSetup]);
  const defaultCadence = useMemo(() => pickDefaultCadence(cadenceOptions), [cadenceOptions]);
  const familyTierOptions = useMemo(() => parseFamilyTiers(stripeSetup || {}), [stripeSetup]);
  const familyMembershipEnabled = familyTierOptions.length > 0;
  const waitlistEnabled = isWaitlistJoinRoutingEnabled(team);
  const selectedFamilyTier = useMemo(() => familyTierOptions.find(tier => tier.id === selectedFamilyTierId) || familyTierOptions[0] || null, [familyTierOptions, selectedFamilyTierId]);
  const familyMembershipMaxMembers = selectedFamilyTier ? Math.max(1, Number(selectedFamilyTier.maxParents || 1) + Number(selectedFamilyTier.maxChildren || 0)) : 1;
  const activeFamilyGroup = useMemo(() => familyMembershipGroups.find(group => group.familyId === selectedFamilyId) || familyMembershipGroups[0] || null, [familyMembershipGroups, selectedFamilyId]);
  const selectableFamilyMemberIds = useMemo(() => new Set((activeFamilyGroup?.members || []).map(member => member.uid)), [activeFamilyGroup]);
  const selectedIndividualTargetFamilyGroup = useMemo(() => familyMembershipGroups.find(group => group.familyId === selectedIndividualTargetFamilyId) || null, [familyMembershipGroups, selectedIndividualTargetFamilyId]);
  const selectableIndividualTargetMemberIds = useMemo(() => new Set((selectedIndividualTargetFamilyGroup?.members || []).map(member => member.uid)), [selectedIndividualTargetFamilyGroup]);

  // Fixed billing has one option; member-choice billing stays unset until chosen.
  useEffect(() => {
    if (stripeSetup?.cadenceMode !== 'member_choice' && !selectedCadence && defaultCadence) {
      setSelectedCadence(defaultCadence);
    }
  }, [stripeSetup?.cadenceMode, defaultCadence, selectedCadence]);
  useEffect(() => {
    if (membershipPlanType !== 'family_tier' || !user?.uid) return;
    if (!selectedFamilyTier && familyTierOptions.length > 0) {
      setSelectedFamilyTierId(familyTierOptions[0].id);
    }
  }, [membershipPlanType, user?.uid, selectedFamilyTier, familyTierOptions]);
  useEffect(() => {
    if (membershipPlanType === 'family_tier' && !familyMembershipEnabled) {
      setMembershipPlanType('individual');
      return;
    }
    if (membershipPlanType !== 'family_tier' || !familyMembershipEnabled || !user?.uid) return;
    if (!activeFamilyGroup && familyMembershipGroups.length > 0) {
      setSelectedFamilyId(familyMembershipGroups[0].familyId);
    }
  }, [membershipPlanType, familyMembershipEnabled, user?.uid, activeFamilyGroup, familyMembershipGroups]);
  const membershipTargetUserIds = useMemo(() => {
    const currentUid = user?.uid;
    if (!currentUid) return [];
    if (membershipPlanType !== 'family_tier') {
      if (familyMembershipGroups.length === 0) return [currentUid];
      const targetUid = typeof selectedIndividualTargetUserId === 'string' ? selectedIndividualTargetUserId.trim() : '';
      if (!targetUid) return [];
      if (targetUid === currentUid) return [currentUid];
      if (!selectedIndividualTargetFamilyGroup) return [];
      if (selectableIndividualTargetMemberIds.size > 0 && !selectableIndividualTargetMemberIds.has(targetUid)) return [];
      return [targetUid];
    }
    const normalized = [];
    const seen = new Set();
    const allowedSet = selectableFamilyMemberIds;
    const canUseAllowedSet = allowedSet.size > 0;
    selectedFamilyMemberIds.forEach(uid => {
      const id = typeof uid === 'string' ? uid.trim() : '';
      if (!id || seen.has(id)) return;
      if (canUseAllowedSet && !allowedSet.has(id)) return;
      seen.add(id);
      normalized.push(id);
    });
    if (!seen.has(currentUid)) {
      seen.add(currentUid);
      normalized.unshift(currentUid);
    }
    if (normalized.length === 0) return [currentUid];
    return normalized.slice(0, familyMembershipMaxMembers);
  }, [user?.uid, membershipPlanType, selectedIndividualTargetUserId, selectedIndividualTargetFamilyGroup, familyMembershipGroups.length, selectedFamilyMemberIds, selectableFamilyMemberIds, selectableIndividualTargetMemberIds, familyMembershipMaxMembers]);
  const membershipTargetCount = Math.max(1, membershipTargetUserIds.length || 1);
  useEffect(() => {
    defaultCadenceRef.current = defaultCadence;
  }, [defaultCadence]);
  useEffect(() => {
    cadenceOptionsRef.current = cadenceOptions;
  }, [cadenceOptions]);
  useEffect(() => {
    membershipTargetCountRef.current = membershipTargetCount;
  }, [membershipTargetCount]);
  const selectedFamilyRoleCounts = useMemo(() => {
    const members = Array.isArray(activeFamilyGroup?.members) ? activeFamilyGroup.members : [];
    const selectedSet = new Set(membershipTargetUserIds);
    let parentCount = 0;
    let childCount = 0;
    members.forEach(member => {
      if (!selectedSet.has(member.uid)) return;
      if (member.role === 'parent') parentCount += 1;
      if (member.role === 'child') childCount += 1;
    });
    return {
      total: parentCount + childCount,
      parentCount,
      childCount
    };
  }, [activeFamilyGroup, membershipTargetUserIds]);
  const familySelectionValidation = useMemo(() => {
    if (membershipPlanType !== 'family_tier') return null;
    if (!selectedFamilyTier) return 'Please select a family tier.';
    const {
      parentCount,
      childCount
    } = selectedFamilyRoleCounts;
    if (parentCount > Number(selectedFamilyTier.maxParents || 1)) {
      return `${selectedFamilyTier.label} allows up to ${selectedFamilyTier.maxParents} parent(s).`;
    }
    if (childCount > Number(selectedFamilyTier.maxChildren || 0)) {
      return `${selectedFamilyTier.label} allows up to ${selectedFamilyTier.maxChildren} child(ren).`;
    }
    return null;
  }, [membershipPlanType, selectedFamilyTier, selectedFamilyRoleCounts]);
  useEffect(() => {
    if (!user?.uid) {
      setSelectedFamilyMemberIds([]);
      return;
    }
    if (membershipPlanType !== 'family_tier') {
      setSelectedFamilyMemberIds([user.uid]);
      return;
    }
    setSelectedFamilyMemberIds(prev => {
      const normalized = [];
      const seen = new Set();
      const allowedSet = selectableFamilyMemberIds;
      const canUseAllowedSet = allowedSet.size > 0;
      (Array.isArray(prev) ? prev : []).forEach(uid => {
        const id = typeof uid === 'string' ? uid.trim() : '';
        if (!id || seen.has(id)) return;
        if (canUseAllowedSet && !allowedSet.has(id)) return;
        seen.add(id);
        normalized.push(id);
      });
      if (!seen.has(user.uid)) {
        normalized.unshift(user.uid);
      }
      return normalized.slice(0, familyMembershipMaxMembers);
    });
  }, [user?.uid, membershipPlanType, selectableFamilyMemberIds, familyMembershipMaxMembers]);

  // Centralised fee calculator (stable function identity)
  const computeFees = useCallback((appliedTags = [], cadenceOverride = null, options = {}) => {
    const setup = stripeSetup || {};
    const {
      baseRate = 0,
      baseRateYearly,
      tagOverrides = {},
      tagOverridesYearly = {},
      platformFeeHandling = 'deduct',
      stripeFeeHandling = 'deduct',
      currency = 'GBP',
      platformFeeMinor
    } = setup;
    const c = cadenceOverride || selectedCadence || defaultCadence || {
      unit: 'year',
      count: 1,
      discountPct: 0
    };
    const effectivePlanType = normalizeMembershipPlanType(options?.planType || membershipPlanType);
    const effectiveTier = effectivePlanType === 'family_tier' ? familyTierOptions.find(tier => tier.id === (options?.planTierId || selectedFamilyTier?.id)) || selectedFamilyTier || familyTierOptions[0] || null : null;
    const memberCount = effectivePlanType === 'family_tier' ? Math.max(1, Number.isFinite(Number(options?.memberCount)) ? Number(options.memberCount) : membershipTargetCount) : 1;
    const pricingBaseRate = effectivePlanType === 'family_tier' ? Number(effectiveTier?.baseRate || 0) : baseRate;
    const pricingBaseRateYearly = effectivePlanType === 'family_tier' ? Number(effectiveTier?.baseRateYearly || 0) : baseRateYearly;
    const pricingTagOverrides = effectivePlanType === 'family_tier' ? {} : tagOverrides;
    const pricingTagOverridesYearly = effectivePlanType === 'family_tier' ? {} : tagOverridesYearly;
    const effectiveAppliedTags = effectivePlanType === 'family_tier' ? [] : appliedTags;
    const basePricing = resolveCycleBasePricing({
      baseRate: pricingBaseRate,
      baseRateYearly: pricingBaseRateYearly,
      tagOverrides: pricingTagOverrides,
      tagOverridesYearly: pricingTagOverridesYearly,
      appliedTags: effectiveAppliedTags,
      preferredTag: membershipPricingTag || '',
      cadenceUnit: c.unit,
      cadenceCount: c.count
    });
    const cycleBaseTotal = Math.round(Number(basePricing.baseForCycle || 0));
    const platformFeeMultiplier = effectivePlanType === 'family_tier' ? memberCount : 1;
    const effectivePlatformFeeMinor = Number.isFinite(Number(platformFeeMinor)) ? Number(platformFeeMinor) * platformFeeMultiplier : platformFeeMinor;
    const shouldApplyMembershipYearProration = shouldProrateMembershipYear({
      setup,
      cadenceUnit: c.unit,
      cadenceCount: c.count,
      usedTagOverride: basePricing.usedTagOverride,
      tagName: basePricing.tagName
    });
    const membershipWindow = getMembershipYearWindow(new Date(), setup);
    const fullCycleFees = calculateTotalFees({
      baseAmount: cycleBaseTotal,
      cadence: {
        unit: c.unit,
        count: c.count,
        discountPct: c.discountPct || 0
      },
      platformFeeHandling,
      stripeFeeHandling,
      currency,
      platformFeeMinor: effectivePlatformFeeMinor
    });
    if (!shouldApplyMembershipYearProration) {
      return {
        ...fullCycleFees,
        memberCount,
        membershipPlanType: effectivePlanType,
        membershipPlanTierId: effectiveTier?.id || null,
        membershipPlanLabel: effectivePlanType === 'family_tier' ? effectiveTier?.label || 'Family Membership' : 'Individual Membership',
        membershipYear: {
          applied: false,
          anchored: false,
          fullPriceWindowActive: false,
          prorationFactor: 1,
          nextRenewalAt: null,
          fullCycle: fullCycleFees
        }
      };
    }
    const factor = membershipWindow.prorationFactor;
    const prorationApplied = factor < 1;
    const proratedFinalAmount = Math.round(fullCycleFees.finalAmount * factor);
    const proratedBaseAmount = Math.round(fullCycleFees.baseAmount * factor);
    const proratedPlatformFee = Math.round(fullCycleFees.platformFee * factor);
    const proratedStripeFee = Math.max(0, proratedFinalAmount - proratedBaseAmount - proratedPlatformFee);
    return {
      baseAmount: proratedBaseAmount,
      platformFee: proratedPlatformFee,
      stripeFee: proratedStripeFee,
      finalAmount: proratedFinalAmount,
      memberCount,
      membershipPlanType: effectivePlanType,
      membershipPlanTierId: effectiveTier?.id || null,
      membershipPlanLabel: effectivePlanType === 'family_tier' ? effectiveTier?.label || 'Family Membership' : 'Individual Membership',
      membershipYear: {
        applied: prorationApplied,
        anchored: true,
        fullPriceWindowActive: membershipWindow.fullPriceWindowActive,
        fullPriceWindowEnd: membershipWindow.fullPriceWindowEnd ? membershipWindow.fullPriceWindowEnd.toISOString() : null,
        prorationFactor: factor,
        nextRenewalAt: membershipWindow.nextYearStart ? membershipWindow.nextYearStart.toISOString() : null,
        fullCycle: fullCycleFees
      }
    };
  }, [stripeSetup, selectedCadence, defaultCadence, membershipTargetCount, membershipPlanType, membershipPricingTag, selectedFamilyTier, familyTierOptions]);
  useEffect(() => {
    computeFeesRef.current = computeFees;
  }, [computeFees]);
  const applyJoinPricingTags = useCallback((tags = [], options = {}) => {
    const normalizedTags = Array.isArray(tags) ? tags.map(tag => typeof tag === 'string' ? tag.trim() : '').filter(Boolean) : [];
    const cadence = selectedCadence || defaultCadence || null;
    const f = computeFees(normalizedTags, cadence, {
      memberCount: Number.isFinite(Number(options?.memberCount)) ? Number(options.memberCount) : membershipTargetCount,
      planType: options?.planType || membershipPlanType,
      planTierId: options?.planTierId || selectedFamilyTier?.id || null
    });
    setLastAppliedTags(normalizedTags);
    setStripeAmount(f.finalAmount / 100);
    setStripeFeeBreakdown(f);
  }, [computeFees, selectedCadence, defaultCadence, membershipTargetCount, membershipPlanType, selectedFamilyTier]);

  // Initial load: team + setup + questions
  useEffect(() => {
    let isMounted = true;
    async function loadTeamAndConfig() {
      try {
        const resolvedUser = await waitForAuthResolution();
        if (!isMounted) return;
        setUser(resolvedUser || null);
        if (!resolvedUser) {
          setError('permission-denied');
          setStatus('form');
          return;
        }

        if (isLearnToRowMode) {
          setStatus(preserveLearnToRowProgressStatus);
        }

        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        if (!teamSnap.exists()) throw new Error('Team not found');
        const teamData = teamSnap.data();
        const documentsEnabled = Boolean(teamData?.modules?.documents?.enabled);
        const setupSnap = await getDoc(doc(db, 'teams', teamId, 'stripe', 'setup'));
        const setupData = setupSnap.exists() ? setupSnap.data() : {};
        const configSnap = await getDoc(doc(db, 'teams', teamId, 'joinFormConfig', 'config'));
        const configData = configSnap.exists() ? configSnap.data() || {} : {};
        const qs = configData.questions || [];
        const docsQuery = query(
          collection(db, 'teams', teamId, 'documents'),
          where('active', '==', true),
          where('category', '==', 'join_requirement')
        );
        const docsSnap = await getDocs(docsQuery);
        const joinDocs = documentsEnabled
          ? getRequiredJoinDocuments(docsSnap.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data()
          })))
          : [];
        if (!isMounted) return;
        setStripeSetup(setupData);
        setTeam({
          id: teamSnap.id,
          ...teamData,
          stripeSetup: setupData
        });
        setQuestions(qs);
        setRequiredJoinDocuments(joinDocs);
        if (isLearnToRowMode) {
          setStatus(preserveLearnToRowProgressStatus);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to load team.');
          setStatus('form'); // fall back to form
        }
      }
    }
    loadTeamAndConfig();
    return () => {
      isMounted = false;
    };
  }, [teamId, isLearnToRowMode]);
  useEffect(() => {
    let isMounted = true;
    async function loadFamilyMembershipOptions() {
      if (!user?.uid) {
        if (!isMounted) return;
        setFamilyMembershipGroups([]);
        setFamilySelectionError(null);
        setFamilySelectionLoading(false);
        return;
      }
      setFamilySelectionLoading(true);
      setFamilySelectionError(null);
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        const familyRoles = userSnap.exists() ? userSnap.data()?.family_roles || {} : {};
        const parentFamilyIds = Object.entries(familyRoles).filter(([, role]) => role === 'parent').map(([familyId]) => familyId).filter(familyId => typeof familyId === 'string' && familyId.trim() !== '');
        if (parentFamilyIds.length === 0) {
          if (!isMounted) return;
          setFamilyMembershipGroups([]);
          setFamilySelectionLoading(false);
          return;
        }
        const groups = [];
        for (const familyId of parentFamilyIds) {
          const [familySnap, membersSnap] = await Promise.all([getDoc(doc(db, 'families', familyId)), getDocs(collection(db, 'families', familyId, 'members'))]);
          if (!familySnap.exists()) continue;
          const rawMembers = membersSnap.docs.map(memberDoc => ({
            uid: memberDoc.id,
            role: String(memberDoc.data()?.role || '').toLowerCase()
          })).filter(member => member.role === 'parent' || member.role === 'child');
          if (rawMembers.length === 0) continue;
          const enrichedMembers = await Promise.all(rawMembers.map(async member => {
            const profileSnap = await getDoc(doc(db, 'users', member.uid));
            const profile = profileSnap.exists() ? profileSnap.data() || {} : {};
            return {
              uid: member.uid,
              role: member.role,
              name: formatFamilyMemberName(profile, member.uid),
              // Team member lookups for non-self users are often blocked by rules.
              // Server-side checkout validation remains authoritative.
              alreadyMember: false,
              isSelf: member.uid === user.uid
            };
          }));
          const members = enrichedMembers.sort((a, b) => {
            if (a.isSelf && !b.isSelf) return -1;
            if (!a.isSelf && b.isSelf) return 1;
            if (a.role !== b.role) return a.role.localeCompare(b.role);
            return a.name.localeCompare(b.name);
          });
          if (members.length === 0) continue;
          groups.push({
            familyId,
            familyName: familySnap.data()?.name || 'Family',
            members
          });
        }
        const sortedGroups = [...groups].sort((a, b) => {
          const nameCompare = String(a.familyName || '').localeCompare(String(b.familyName || ''), undefined, {
            sensitivity: 'base'
          });
          if (nameCompare !== 0) return nameCompare;
          return String(a.familyId || '').localeCompare(String(b.familyId || ''), undefined, {
            sensitivity: 'base'
          });
        });
        if (!isMounted) return;
        setFamilyMembershipGroups(sortedGroups);
        setSelectedFamilyId(prev => {
          if (prev && sortedGroups.some(group => group.familyId === prev)) return prev;
          return sortedGroups[0]?.familyId || '';
        });
      } catch (err) {
        if (!isMounted) return;
        setFamilyMembershipGroups([]);
        setFamilySelectionError('Could not load family members.');
      } finally {
        if (isMounted) setFamilySelectionLoading(false);
      }
    }
    loadFamilyMembershipOptions();
    return () => {
      isMounted = false;
    };
  }, [user?.uid, teamId]);

  // Auth & pending membership watcher (after setup is known)
  useEffect(() => {
    if (!stripeSetup) {
      return;
    }
    const unsubscribe = auth.onAuthStateChanged(async currentUser => {
      setUser(currentUser);

      // Helper: set fee state safely
      const setFeesFrom = (tagsArr, cadence, opts = {}) => {
        const calculator = computeFeesRef.current;
        if (typeof calculator !== 'function') return;
        const f = calculator(tagsArr, cadence, opts);
        setStripeAmount(f.finalAmount / 100);
        setStripeFeeBreakdown(f);
        setLastAppliedTags(tagsArr);
      };

      // Not logged in → default form + fees using default cadence
      if (!currentUser) {
        setHasPaid(false);
        setHasPendingMemberDoc(false);
        setPendingMemberStatus(null);
        setMembershipPricingTag('');
        setPreassignedTags([]);
        setStatus('form');
        const cadence = selectedCadenceRef.current || defaultCadenceRef.current;
        if (cadence) setFeesFrom([], cadence, {
          memberCount: 1,
          planType: 'individual'
        });
        return;
      }

      // Logged in: Check if already a member
      let paid = false;
      try {
        const memberSnap = await getDoc(doc(db, 'teams', teamId, 'members', currentUser.uid));
        paid = memberSnap.exists();
      } catch (err) {
        if (err?.code !== 'permission-denied') {}
        paid = false;
      }
      setHasPaid(paid);

      if (isLearnToRowMode) {
        setHasPendingMemberDoc(false);
        setPendingMemberStatus(null);
        setMembershipPricingTag('');
        setPreassignedTags([]);
        setStatus(preserveLearnToRowProgressStatus);
        return;
      }

      // Load pending app (tags + saved cadence)
      try {
        const pendingRef = doc(db, 'teams', teamId, 'pendingMembers', currentUser.uid);
        const pendingSnap = await getDoc(pendingRef);
        const pendingData = pendingSnap.exists ? pendingSnap.data() || {} : null;
        const persistedInviteTags = parseTagNames(pendingData?.preassignedTags ?? pendingData?.tags);
        const appliedTags = Array.from(new Set([...persistedInviteTags, ...parseTagNames(pendingData?.appliedTags)]));
        const hasPendingPlanField = typeof pendingData?.membershipPlanType === 'string' && pendingData.membershipPlanType.trim() !== '';
        const inferredPendingPlanType = hasPendingPlanField ? normalizeMembershipPlanType(pendingData.membershipPlanType) : 'individual';
        const pendingPlanType = pendingSnap.exists() ? inferredPendingPlanType : membershipPlanTypeRef.current;
        const pendingTargetIds = pendingSnap.exists()
          ? parseTargetIds(pendingData?.membershipTargetUserIds, pendingPlanType === 'family_tier' ? currentUser.uid : '', pendingPlanType === 'family_tier')
          : [currentUser.uid];
        const pendingPlanTierId = pendingSnap.exists() ? pendingData?.membershipPlanTierId || '' : selectedFamilyTierIdRef.current;
        const pendingPricingTag = pendingSnap.exists() ? typeof pendingData?.membershipPricingTag === 'string' ? pendingData.membershipPricingTag.trim() : '' : '';
        const pendingFamilyId = pendingSnap.exists() ? pendingData?.membershipTargetFamilyId || '' : '';
        if (pendingSnap.exists()) {
          setPreassignedTags(persistedInviteTags);
          if (pendingPlanType === 'family_tier') {
            setMembershipPlanType('family_tier');
            setSelectedFamilyTierId(pendingPlanTierId);
          } else {
            setMembershipPlanType('individual');
            setSelectedFamilyTierId('');
          }
          setMembershipPricingTag(pendingPricingTag || '');
          if (pendingFamilyId) {
            setSelectedFamilyId(pendingFamilyId);
          }
          if (pendingPlanType === 'family_tier') {
            setSelectedFamilyMemberIds(pendingTargetIds);
          } else {
            setSelectedIndividualTargetFamilyId(pendingFamilyId || '');
            setSelectedIndividualTargetUserId(pendingTargetIds[0] || currentUser.uid);
            setSelectedFamilyMemberIds([currentUser.uid]);
          }
        } else {
          setPreassignedTags([]);
        }

        // pick cadence (pending → current → default)
        let cadence = selectedCadenceRef.current || defaultCadenceRef.current;
        const pendingCadence = pendingSnap.exists() ? pendingSnap.data().selectedCadence || null : null;
        if (pendingCadence) {
          // Try to map to our known option; otherwise construct compatible object
          const match = (cadenceOptionsRef.current || []).find(o => o.unit === pendingCadence.unit && o.count === pendingCadence.count) || null;
          cadence = match || {
            unit: pendingCadence.unit,
            count: pendingCadence.count,
            discountPct: pendingCadence.discountPct || 0,
            key: pendingCadence.unit === 'month' ? 'monthly' : 'yearly',
            label: pendingCadence.unit === 'month' ? 'Monthly' : 'Yearly'
          };
        }

        // Ensure selectedCadence reflects what we'll use
        const currentSelectedCadence = selectedCadenceRef.current;
        if (cadence && (!currentSelectedCadence || currentSelectedCadence.unit !== cadence.unit || currentSelectedCadence.count !== cadence.count || (currentSelectedCadence.discountPct || 0) !== (cadence.discountPct || 0))) {
          setSelectedCadence(cadence);
        }

        // Fees
        setFeesFrom(appliedTags, cadence, {
          memberCount: pendingPlanType === 'family_tier' ? pendingTargetIds.length || 1 : 1,
          planType: pendingPlanType,
          planTierId: pendingPlanTierId || null
        });
        const hasJoinFormQuestions = (questionsRef.current || []).some(q => !isPricingQuestion(q));
        const hasRequiredJoinDocuments = (requiredJoinDocumentsRef.current || []).length > 0;
        const requiredNonPricingGroups = buildRequiredTagGroups(team?.tags || [], {
          pricingTagNames: getPricingTagNames(stripeSetup || {})
        }).filter(group => !group.isPricingGroup);
        const requiresApproval = team?.availability?.joinable === 'On Approval';
        const requiresJoinFormStep = hasJoinFormQuestions || hasRequiredJoinDocuments || requiresApproval || requiredNonPricingGroups.length > 0;
        const requiresMembershipTypeStep = true;
        const hasAcceptedRequiredJoinDocuments = hasAcceptedAllRequiredJoinDocuments(requiredJoinDocumentsRef.current || [], pendingData || {});

        // Status
        if (pendingSnap.exists()) {
          setHasPendingMemberDoc(true);
          const statusFromDoc = normalizePendingMemberStatus(pendingSnap.data().status);
          setPendingMemberStatus(statusFromDoc || null);
          if (statusFromDoc === 'approved') {
            const nextStatus = hasAcceptedRequiredJoinDocuments ? 'payment' : 'form';
            setStatus(nextStatus);
          } else if (statusFromDoc === 'invited') {
            if (requiresMembershipTypeStep) {
              setStatus('membership_type');
            } else {
              setStatus(requiresJoinFormStep ? 'form' : 'payment');
            }
          } else if (statusFromDoc === 'waitlisted') {
            setStatus('waitlisted');
          } else {
            setStatus('waiting');
          }
        } else {
          setHasPendingMemberDoc(false);
          setPendingMemberStatus(null);
          setMembershipPricingTag('');
          setPreassignedTags([]);
          if (requiresMembershipTypeStep) {
            setStatus('membership_type');
          } else {
            setStatus(requiresJoinFormStep ? 'form' : 'payment');
          }
        }
      } catch (err) {
        setPreassignedTags([]);
        const cadence = selectedCadenceRef.current || defaultCadenceRef.current;
        setFeesFrom([], cadence || null, {
          memberCount: membershipTargetCountRef.current || 1,
          planType: membershipPlanTypeRef.current,
          planTierId: selectedFamilyTierIdRef.current || null
        });
        setStatus('form');
      }
    });
    return () => unsubscribe();
    // deps: everything we read inside that can change outside the handler
  }, [teamId, team, stripeSetup, requiredJoinDocuments, familyMembershipGroups.length, isLearnToRowMode]);

  // Recompute fees whenever cadence or setup changes (using last known tags)
  useEffect(() => {
    if (!stripeSetup || !selectedCadence) return;
    const f = computeFees(lastAppliedTags, selectedCadence, {
      memberCount: membershipTargetCount,
      planType: membershipPlanType,
      planTierId: selectedFamilyTierId || null
    });
    setStripeAmount(f.finalAmount / 100);
    setStripeFeeBreakdown(f);
  }, [stripeSetup, selectedCadence, lastAppliedTags, computeFees, membershipTargetCount, membershipPlanType, selectedFamilyTierId]);

  // legacy flags (kept for UI toggles if used elsewhere)
  const isManualPayment = stripeSetup?.paymentMode === 'manual';
  const isRecurringPayment = stripeSetup?.paymentMode === 'recurring';
  const setFamilyTargetSelection = useCallback(nextIds => {
    const currentUid = user?.uid;
    if (!currentUid) return;
    if (membershipPlanType !== 'family_tier') {
      setSelectedFamilyMemberIds([currentUid]);
      return;
    }
    const values = Array.isArray(nextIds) ? nextIds : [];
    const normalized = [];
    const seen = new Set();
    const allowedSet = selectableFamilyMemberIds;
    const canUseAllowedSet = allowedSet.size > 0;
    values.forEach(uid => {
      const id = typeof uid === 'string' ? uid.trim() : '';
      if (!id || seen.has(id)) return;
      if (canUseAllowedSet && !allowedSet.has(id)) return;
      seen.add(id);
      normalized.push(id);
    });
    if (!seen.has(currentUid)) normalized.unshift(currentUid);
    setSelectedFamilyMemberIds(normalized.slice(0, familyMembershipMaxMembers));
  }, [user?.uid, membershipPlanType, selectableFamilyMemberIds, familyMembershipMaxMembers]);
  const toggleFamilyTargetMember = useCallback((memberUid, checked) => {
    const currentUid = user?.uid;
    if (!currentUid) return;
    if (membershipPlanType !== 'family_tier') return;
    const uid = typeof memberUid === 'string' ? memberUid.trim() : '';
    if (!uid) return;
    if (uid === currentUid) return; // payer must remain selected

    setSelectedFamilyMemberIds(prev => {
      const values = Array.isArray(prev) ? [...prev] : [currentUid];
      const exists = values.includes(uid);
      if (checked) {
        if (exists) return values;
        if (values.length >= familyMembershipMaxMembers) return values;
        return [...values, uid];
      }
      if (!exists) return values;
      return values.filter(id => id !== uid);
    });
  }, [user?.uid, membershipPlanType, familyMembershipMaxMembers]);
  const value = useMemo(() => ({
    team,
    teamId,
    user,
    status,
    setStatus,
    formResponses,
    setFormResponses,
    stripeSetup,
    familyMembershipEnabled,
    waitlistEnabled,
    familyTierOptions,
    familyMembershipMaxMembers,
    membershipPlanType,
    setMembershipPlanType,
    selectedFamilyTierId,
    setSelectedFamilyTierId,
    selectedFamilyTier,
    familyMembershipGroups,
    selectedFamilyId,
    setSelectedFamilyId,
    selectedIndividualTargetFamilyId,
    setSelectedIndividualTargetFamilyId,
    selectedFamilyMemberIds: membershipTargetUserIds,
    selectedIndividualTargetUserId,
    setSelectedIndividualTargetUserId,
    setFamilyTargetSelection,
    toggleFamilyTargetMember,
    familySelectionLoading,
    familySelectionError,
    familySelectionValidation,
    membershipTargetUserIds,
    membershipTargetCount,
    pendingMemberStatus,
    setPendingMemberStatus,
    joinPaymentSummary,
    setJoinPaymentSummary,
    membershipPricingTag,
    setMembershipPricingTag,
    membershipDiscountCode,
    setMembershipDiscountCode,
    preassignedTags,
    // fees
    stripeAmount,
    stripeFeeBreakdown,
    // cadence
    cadenceMode: stripeSetup?.cadenceMode || 'fixed',
    cadenceOptions,
    selectedCadence,
    setSelectedCadence,
    applyJoinPricingTags,
    // misc
    isManualPayment,
    isRecurringPayment,
    error,
    questions,
    requiredJoinDocuments,
    hasPaid,
    hasPendingMemberDoc,
    isDemoMode,
    isPrelaunchCommitteeMode: Boolean(
      verifiedPrelaunchCommittee
      && user?.uid
      && Array.isArray(team?.admins)
      && team.admins.includes(user.uid)
      && team?.availability?.live !== true
    )
  }), [team, teamId, user, status, formResponses, stripeSetup, stripeAmount, stripeFeeBreakdown, joinPaymentSummary, familyMembershipEnabled, waitlistEnabled, familyTierOptions, familyMembershipMaxMembers, membershipPlanType, selectedFamilyTierId, selectedFamilyTier, familyMembershipGroups, selectedFamilyId, selectedIndividualTargetFamilyId, selectedIndividualTargetUserId, membershipTargetUserIds, membershipTargetCount, membershipPricingTag, membershipDiscountCode, preassignedTags, setFamilyTargetSelection, toggleFamilyTargetMember, familySelectionLoading, familySelectionError, familySelectionValidation, cadenceOptions, selectedCadence, applyJoinPricingTags, isManualPayment, isRecurringPayment, error, questions, requiredJoinDocuments, hasPaid, hasPendingMemberDoc, pendingMemberStatus, isDemoMode, verifiedPrelaunchCommittee]);
  return <JoinContext.Provider value={value}>{children}</JoinContext.Provider>;
}
export function useJoin() {
  return useContext(JoinContext);
}
